/* global axios */
import ApiClient from './ApiClient';

class PipelinesAPI extends ApiClient {
  constructor() {
    super('pipelines', { accountScoped: true });
  }

  /**
   * Get all pipelines for an account
   * @param {Number} accountId - Account ID
   * @returns {Promise}
   */
  get(accountId) {
    return axios.get(`/api/v1/accounts/${accountId}/pipelines`);
  }

  /**
   * Get a single pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @returns {Promise}
   */
  show(accountId, pipelineId) {
    return axios.get(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}`);
  }

  /**
   * Create a new pipeline
   * @param {Number} accountId - Account ID
   * @param {Object} pipelineData - Pipeline data
   * @returns {Promise}
   */
  create(accountId, pipelineData) {
    return axios.post(`/api/v1/accounts/${accountId}/pipelines`, pipelineData);
  }

  /**
   * Create a pipeline from a template
   * @param {Number} accountId - Account ID
   * @param {String} templateId - Template ID
   * @returns {Promise}
   */
  createFromTemplate(accountId, templateId) {
    return axios.post(`/api/v1/accounts/${accountId}/pipelines/from_template`, {
      template_id: templateId,
    });
  }

  /**
   * Update a pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @param {Object} pipelineData - Pipeline data
   * @returns {Promise}
   */
  update(accountId, pipelineId, pipelineData) {
    return axios.patch(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}`, pipelineData);
  }

  /**
   * Delete a pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @returns {Promise}
   */
  delete(accountId, pipelineId) {
    return axios.delete(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}`);
  }

  /**
   * Get all stages for a pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @returns {Promise}
   */
  getStages(accountId, pipelineId) {
    return axios.get(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}/stages`);
  }

  /**
   * Create a new stage in a pipeline
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @param {Object} stageData - Stage data
   * @returns {Promise}
   */
  createStage(accountId, pipelineId, stageData) {
    return axios.post(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}/stages`, stageData);
  }

  /**
   * Update a stage
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @param {Number} stageId - Stage ID
   * @param {Object} stageData - Stage data
   * @returns {Promise}
   */
  updateStage(accountId, pipelineId, stageId, stageData) {
    return axios.patch(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}/stages/${stageId}`, stageData);
  }

  /**
   * Delete a stage
   * @param {Number} accountId - Account ID
   * @param {Number} pipelineId - Pipeline ID
   * @param {Number} stageId - Stage ID
   * @returns {Promise}
   */
  deleteStage(accountId, pipelineId, stageId) {
    return axios.delete(`/api/v1/accounts/${accountId}/pipelines/${pipelineId}/stages/${stageId}`);
  }

  reorderStages(accountId, pipelineId, stages) {
    return axios.patch(
      `/api/v1/accounts/${accountId}/pipelines/${pipelineId}/stages/reorder`,
      { stages }
    );
  }
}

export default new PipelinesAPI();